import PocketBase from "pocketbase";

// Create PocketBase instance
const pb = new PocketBase("https://db.togx.org/");

// Don't load from cookie automatically
// pb.authStore.loadFromCookie(document.cookie); // Remove this line

// Create event system for avatar updates
const avatarChangeEvent = new EventTarget();

// Export functions
export { pb };

export const notifyAvatarChange = () => {
  avatarChangeEvent.dispatchEvent(new Event("avatarChange"));
};

export const isUserValid = () => pb.authStore.isValid;

export const getCurrentUser = () => {
  try {
    // Cache the auth check result
    const isValid = pb.authStore.isValid;
    if (!isValid) {
      return null;
    }

    // Cache the model to prevent multiple accesses
    const model = pb.authStore.model;
    if (model) {
      // Return a memoized object
      return Object.freeze({
        ...model,
        is_admin: model.is_admin || false,
      });
    }
    return null;
  } catch (err) {
    console.error("Error getting current user");
    return null;
  }
};

// Modified logout function to prevent recursion
export const logout = async () => {
  try {
    // First, log out from PocketBase if we're authenticated
    if (pb.authStore.isValid) {
      await pb.authStore.clear();
    }

    // Clear all possible cookie variations
    const domains = [
      window.location.hostname,
      `.${window.location.hostname}`,
      "",
    ];
    const paths = ["/", ""];

    domains.forEach((domain) => {
      paths.forEach((path) => {
        document.cookie = `pb_auth=; path=${path}${
          domain ? `;domain=${domain}` : ""
        }; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; samesite=strict`;
      });
    });

    // Clear localStorage and sessionStorage
    localStorage.clear();
    sessionStorage.clear();

    // Clear the auth store without directly setting model
    pb.authStore.clear();

    // Remove any existing onChange listeners
    pb.authStore.onChange(() => {});
  } catch (error) {
    console.error("Logout error");
  }
};

// Remove or modify the onChange handler that sets cookies
const DISCORD_SERVER_ID = "963454567490654281"; // Replace with your server ID

export const verifyDiscordServerMembership = async (authData) => {
  try {
    if (!authData?.meta?.accessToken) {
      console.error("No access token available");
      return false;
    }

    // Get member data first
    const memberResponse = await fetch(
      `https://discord.com/api/v10/users/@me/guilds/${DISCORD_SERVER_ID}/member`,
      {
        headers: {
          Authorization: `Bearer ${authData.meta.accessToken}`,
        },
      }
    );

    if (memberResponse.status === 200) {
      const memberData = await memberResponse.json();

      // Get role information using bot token
      const botResponse = await fetch(`/api/discord/roles`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          roleIds: memberData.roles,
        }),
      });

      if (botResponse.ok) {
        const roleNames = await botResponse.json();
        // Replace role IDs with role names
        memberData.roles = roleNames.roles;
      }

      if (pb.authStore.isValid && pb.authStore.model?.id) {
        try {
          const updateData = {
            discord_data: JSON.stringify(memberData),
            discord_id: memberData.user?.id || null,
          };

          await pb
            .collection("users")
            .update(pb.authStore.model.id, updateData);
        } catch (updateError) {
          console.error("Failed to update Discord data");
        }
      }

      return true;
    } else {
      console.error(await memberResponse.text());
      return false;
    }
  } catch (error) {
    console.error("Error verifying server membership");
    return false;
  }
};

// Add this to verify token expiration
pb.authStore.onChange((token, model) => {
  if (!token) return; // Skip if no token (prevents recursion during logout)

  const isValid = pb.authStore.isValid;
  if (!isValid) {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    document.cookie = pb.authStore.exportToCookie({
      expires: date,
    });
  }
});

// Add these at the top of the file with other imports
const roleCache = new Map();
const lastRoleUpdate = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const BATCH_SIZE = 10; // Number of requests to process at once
const BATCH_INTERVAL = 1000; // Time between batches in milliseconds

export const getDiscordTopRole = async (user) => {
  try {
    if (!user.discord_id || !user.discord_data) {
      return "No Discord";
    }

    // Check cache first
    const cachedRole = roleCache.get(user.discord_id);
    const lastUpdate = lastRoleUpdate.get(user.discord_id);
    if (cachedRole && lastUpdate && Date.now() - lastUpdate < CACHE_DURATION) {
      return cachedRole;
    }

    // Get roles from stored Discord data in PocketBase
    const discordData =
      typeof user.discord_data === "string"
        ? JSON.parse(user.discord_data)
        : user.discord_data;

    if (!discordData || !discordData.roles || discordData.roles.length === 0) {
      const role = "Member";
      roleCache.set(user.discord_id, role);
      lastRoleUpdate.set(user.discord_id, Date.now());
      return role;
    }

    // Sort roles by position (higher position = higher role)
    const sortedRoles = discordData.roles.sort(
      (a, b) => b.position - a.position
    );
    const topRole = sortedRoles[0].name || "Member";

    // Update cache
    roleCache.set(user.discord_id, topRole);
    lastRoleUpdate.set(user.discord_id, Date.now());

    return topRole;
  } catch (error) {
    console.error("Error getting Discord role");
    return "Error";
  }
};

// Add this function to process users in batches
export const fetchUserRolesBatch = async (users) => {
  const results = new Map();

  for (let i = 0; i < users.length; i += BATCH_SIZE) {
    const batch = users.slice(i, i + BATCH_SIZE);
    await Promise.all(
      batch.map(async (user) => {
        const role = await getDiscordTopRole(user);
        results.set(user.id, role);
      })
    );

    if (i + BATCH_SIZE < users.length) {
      await new Promise((resolve) => setTimeout(resolve, BATCH_INTERVAL));
    }
  }

  return results;
};

export const updateDiscordData = async (userId, memberData) => {
  try {
    if (!userId || !memberData) {
      throw new Error("Missing userId or memberData");
    }

    // Prepare the data structure exactly as received from Discord API
    const data = {
      discord_data: JSON.stringify({
        avatar: memberData.avatar,
        banner: memberData.banner,
        communication_disabled_until: memberData.communication_disabled_until,
        flags: memberData.flags,
        joined_at: memberData.joined_at,
        nick: memberData.nick,
        pending: memberData.pending,
        premium_since: memberData.premium_since,
        roles: memberData.roles,
        unusual_dm_activity_until: memberData.unusual_dm_activity_until,
        user: memberData.user,
        mute: memberData.mute,
        deaf: memberData.deaf,
        bio: memberData.bio,
      }),
      discord_id: memberData.user?.id || null,
    };

    // Update the user record
    const record = await pb.collection("users").update(userId, data);

    return record;
  } catch (error) {
    throw error;
  }
};

// Add this after the pb instance creation
pb.authStore.onChange(() => {
  console.log("Auth state changed:", {
    isValid: pb.authStore.isValid,
    token: !!pb.authStore.token,
    userId: pb.authStore.model?.id,
  });
});

// Define admin role names or IDs
const ADMIN_ROLES = [
  "963454605247799386",
  "963454622733828156",
  "1083963083308937289",
]; // Adjust based on your Discord server roles

export const hasAdminPermission = (user) => {
  // First check if user has is_admin flag set
  if (user?.is_admin) {
    return true;
  }

  // Then check Discord roles if available
  try {
    if (user?.discord_data) {
      const discordData =
        typeof user.discord_data === "string"
          ? JSON.parse(user.discord_data)
          : user.discord_data;

      if (discordData?.roles) {
        // Check if any of the user's roles match admin roles
        return discordData.roles.some((role) =>
          typeof role === "string"
            ? ADMIN_ROLES.includes(role)
            : ADMIN_ROLES.includes(role.name)
        );
      }
    }
    return false;
  } catch (error) {
    return false;
  }
};
